/* eslint-disable complexity */
import React, { useState } from 'react';
import { useTranslations } from '@unisporkal/localization';
import { RawHtml } from '@unisporkal/utilities';
import { useSiteMap } from '@unisporkal/sites';
import type { LoadedLicenseHistory } from 'types/isomorphic';
import DownArrow from '../../../icons/DownArrow.svg?icon';
import RightArrow from '../../../icons/RightArrow.svg?icon';
import { Agreement } from '../../../../shared/models/agreement';
import styles from './MiniDownloadHistory.module.scss';
import useOnLoad from '../../../hooks/queries/useOnLoad';
import { getRfSubscriptionLabelKey } from '../../../shared/rfSubscriptionLabels';

interface UseOnLoadResponse {
  response: {
    licenseHistory: LoadedLicenseHistory;
    config: {
      options?: Agreement[];
    };
  };
}

const MiniDownloadHistory = () => {
  const t = useTranslations();
  const siteMap = useSiteMap();
  const {
    response: {
      licenseHistory: {
        historyAsset,
        historyAssetCount = 0,
        historyAssetView,
      } = {},
      config: { options } = {},
    } = { licenseHistory: {}, config: {} },
  }: UseOnLoadResponse = useOnLoad();
  const [expanded, setExpanded] = useState(false);

  const getRfSubscriptionLabel = () => {
    const rfOption = options?.find(
      (option) => option.type === 'RoyaltyFreeSubscription'
    );
    return t(getRfSubscriptionLabelKey(rfOption?.subFamily));
  };

  const localizeProductType = (type) => {
    if (type === 'image_pack') {
      return t('ultrapack');
    }
    if (type === 'rf_subscription') {
      return getRfSubscriptionLabel();
    }
    if (type === 'a_la_carte') {
      return null;
    }
    return t(type);
  };

  const productType = historyAsset
    ? `${localizeProductType(historyAsset.downloadProductType)} ${
        historyAsset.agreementCustomName || ''
      }`
    : '';

  const downloadBy = () => {
    if (historyAsset?.downloadBy) {
      return (
        <tr>
          <td className={styles.item}>{t('member_colon')}</td>
          <td className={styles.item}>{historyAsset.downloadBy}</td>
        </tr>
      );
    }
    return null;
  };

  const projectCode = historyAsset?.projectCode ? (
    <tr>
      <td className={styles.item}>{t('project_code_colon')}</td>
      <td className={styles.item}>{historyAsset.projectCode}</td>
    </tr>
  ) : null;

  const downloadNotes = () => {
    if (historyAsset?.downloadNotes) {
      return (
        <tr>
          <td className={styles.item}>{t('notes_colon')}</td>
          <td className={styles.item}>{historyAsset.downloadNotes}</td>
        </tr>
      );
    }
    return null;
  };

  const historyTableView = () => {
    if (
      historyAsset?.downloadBy ||
      historyAsset?.projectCode ||
      historyAsset?.downloadNotes
    ) {
      return (
        <table
          className={expanded ? styles.historyTable : styles.hidden}
          data-testid="history-table-view"
        >
          <tbody>
            {downloadBy()}
            {projectCode}
            {downloadNotes()}
          </tbody>
        </table>
      );
    }
    return null;
  };

  const viewAllUrl = () => {
    if (historyAsset?.downloadProductType === 'a_la_carte') {
      return `/account/purchasehistory/company?search_by=AssetNumber&search_value=${historyAsset.assetId}`;
    }
    return `/account/download/company/all?search_by=id&asset_ids=${historyAsset?.assetId}&details=true`;
  };

  const notificationText =
    historyAsset?.downloadProductType === 'a_la_carte'
      ? t('last_purchased_date', {
          date: historyAsset.dateCreatedFormatted,
          ...(historyAssetView === 'Company'
            ? {
                begin_link: `<a href='/sales-order/${historyAsset.orderId}'>`,
                end_link: '</a>',
              }
            : {
                begin_link: '',
                end_link: '',
              }),
        })
      : t('date_with_product_type', {
          date: historyAsset?.dateCreatedFormatted,
          product_type: productType,
        });
  const viewAllLink = () => {
    if (historyAssetCount > 1 && historyAssetView === 'Company') {
      return (
        <div
          className={expanded ? styles.moreLink : styles.hidden}
          data-testid="view-all-link"
        >
          <a
            className={styles.moreLinkAnchor}
            href={siteMap.cleanPath(viewAllUrl())}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('view_all')}
            <RightArrow className={styles.moreLinkAnchorArrow} />
          </a>
        </div>
      );
    }
    return null;
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      setExpanded(!expanded);
    }
  };

  const handleClick = () => {
    setExpanded(!expanded);
  };

  const inApplicableLicenseStatuses = ['downloadedfailed', 'cancelled'];
  const isPreviouslyDownloaded = !inApplicableLicenseStatuses.includes(
    historyAsset?.downloadStatus?.toLowerCase() || ''
  );

  if (historyAsset && isPreviouslyDownloaded) {
    return (
      <div className={styles.container}>
        <div
          className={styles.content}
          data-testid="mini-download-history"
        >
          <div
            className={`${styles.expandButton} ${
              expanded ? styles.expanded : ''
            }`}
            onClick={handleClick}
            onKeyPress={handleKeyPress}
            role="button"
            tabIndex={0}
          >
            <DownArrow />
          </div>
          <RawHtml className={styles.entry}>{notificationText}</RawHtml>
          {historyTableView()}
          {viewAllLink()}
        </div>
      </div>
    );
  }
  return null;
};

export default MiniDownloadHistory;
